/**
 * One way to say "that worked" or "that did not", from anywhere.
 *
 * A press that changes something gets an answer the person cannot miss: a
 * checkmark that draws itself, a few words, and a tick in the hand where the
 * phone can give one. It floats over whatever screen is showing and leaves on
 * its own. Inline status text stays for detail; this is the confirmation.
 */
export interface Toast {
  readonly id: number;
  readonly kind: 'ok' | 'err';
  readonly text: string;
}

type Listener = (toast: Toast | null) => void;

let listener: Listener | null = null;
let haptic: (() => void) | null = null;
let counter = 0;
let timer: number | null = null;

export function onToast(l: Listener | null): void {
  listener = l;
}

/** The host's success tick, registered once the platform is known. */
export function setHaptic(f: () => void): void {
  haptic = f;
}

function show(kind: Toast['kind'], text: string): void {
  counter += 1;
  listener?.({ id: counter, kind, text });
  if (kind === 'ok') haptic?.();
  if (timer !== null) window.clearTimeout(timer);
  // Long enough to read, short enough not to be in the way. Errors linger.
  timer = window.setTimeout(
    () => {
      listener?.(null);
      timer = null;
    },
    kind === 'ok' ? 2200 : 3600,
  );
}

export const toast = {
  ok: (text: string): void => {
    show('ok', text);
  },
  err: (text: string): void => {
    show('err', text);
  },
};
